<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку комплектаций'"
                    :to="router.resolve({ name: 'HandbookComplectations' }).href"
                />
            </div>

            <div class="content-header__title">Редактирование комплектации</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isComplectationLoading" class="editable" />

        <template v-else>
            <div class="content__body">
                <div class="form__content">
                    <div class="form__fields">
                        <UIBrandImageableRadio
                            v-model="formBrand"
                            :error-message="''"
                        />

                        <div class="form__field-group form__field-group--by-2">
                            <UISelect
                                id="model"
                                v-model="form.model_id"
                                :options="modelOptions"
                                :error-message="errors?.model_id?.join() ?? ''"
                                label="Модель*"
                                searchable
                            />

                            <UIInput
                                id="model_code"
                                v-model="form.code"
                                :error-message="errors?.code?.join() ?? ''"
                                label="Код комплектации*"
                                replace-pattern="[^0-9a-zA-Z]"
                            />
                        </div>
                    </div>

                    <ComplectationsOptionsForm v-model="form.complectation_option" />
                </div>
            </div>

            <div class="content__footer">
                <div class="form__actions form__actions--2-blocks">
                    <div>
                        <UISimpleButton
                            button-type="danger"
                            @clicked="onDeleteComplectation"
                        >
                            <template #icon>
                                <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                            </template>
                            Удалить комплектацию
                        </UISimpleButton>
                    </div>

                    <div class="form__actions">
                        <UISimpleButton
                            button-type="outlined"
                            @clicked="goToComplectationsPage"
                        >
                            Выйти без сохранения
                        </UISimpleButton>

                        <UISimpleButton
                            :loading="saveLoading"
                            @clicked="onSaveComplectation"
                        >
                            Сохранить
                        </UISimpleButton>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { onMounted, ref, computed, watch, inject, h } from 'vue';
    import { useRouter, useRoute } from 'vue-router';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import {
        Complectation,
        ComplectationUIUpdateForm,
        ComplectationUpdateForm,
        ComplectationUpdateFormErrors,
        fillComplectationUIUpdateFormFromRequestComplectation,
        makeComplectationEmptyUICreateForm,
        mapComplectationUIUpdateFormToRequestUpdateForm,
    } from '@/types/complectation.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useComplectationRepository from '@/repositories/complectation/useComplectationRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUnsavedDataWarning from '@/plugins/useUnsavedDataWarning.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIBrandImageableRadio from '@/components/ui/UIBrandImageableRadio/UIBrandImageableRadio.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import ComplectationConfirmDelete from '@/components/complectations/ComplectationConfirmDelete/ComplectationConfirmDelete.vue';
    import ComplectationsOptionsForm from '@/components/complectations/ComplectationsOptionsForm/ComplectationsOptionsForm.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const router = useRouter();
    const route = useRoute();
    const { showSuccess } = useNotifications();

    const {
        updateComplectation,
        getComplectation,
        deleteComplectation,
    } = useComplectationRepository();
    const { getAllActiveModels } = useModelRepository();
    const { isLoading: isComplectationLoading, setLoading: setComplectationLoading } = useLoading();
    const { setDirty } = useUnsavedDataWarning();

    const arModels = ref<Model[]>([]);
    const form = ref<ComplectationUIUpdateForm>(makeComplectationEmptyUICreateForm());
    const formBrandId = ref<number | undefined>(undefined);
    const saveLoading = ref<boolean>(false);
    const errors = ref<ComplectationUpdateFormErrors | null>(null);

    const modelOptions = computed<SelectOption[]>(() => {
        if (formBrandId.value === undefined) {
            return [];
        }

        return arModels.value
            .filter((model: Model) => formBrandId.value === undefined || model.brand_id === formBrandId.value)
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    const formBrand = computed<string>({
        get() {
            if (formBrandId.value === 1) {
                return 'hyundai';
            }

            if (formBrandId.value === 2) {
                return 'genesis';
            }

            return '';
        },
        set(brandValue: string) {
            if (brandValue === 'hyundai') {
                formBrandId.value = 1;
            }
            else if (brandValue === 'genesis') {
                formBrandId.value = 2;
            }

            if (arModels.value.length > 0) {
                const selectedModel = arModels.value.find((model: Model) => model.id === form.value.model_id);
                if (selectedModel?.brand_id !== formBrandId.value) {
                    form.value.model_id = undefined;
                }
            }
        },
    });

    const complectationId = route.params.complectation_id as unknown as number;

    watch(form, () => {
        setDirty(true);
    }, { deep: true });

    function onSaveComplectation(): void {
        saveLoading.value = true;
        errors.value = null;

        const updateForm: ComplectationUpdateForm = mapComplectationUIUpdateFormToRequestUpdateForm(form.value);
        updateComplectation(complectationId, updateForm)
            .then(() => {
                setDirty(false);

                showSuccess({
                    title: 'Изменения в комплектации сохранены',
                });

                goToComplectationsPage();
            })
            .catch((updateErrors: ComplectationUpdateFormErrors) => {
                errors.value = updateErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function onDeleteComplectation(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () => {
                    const modelName = form.value.model_id
                        ? (modelOptions.value.find((option) => option.value === form.value.model_id)?.label) as string
                        : '';

                    let brandName = '';
                    switch (formBrandId.value) {
                        case 1:
                            brandName = 'Hyundai';
                            break;
                        case 2:
                            brandName = 'Genesis';
                    }

                    return h(ComplectationConfirmDelete, {
                        complectation: {
                            id: complectationId,
                            model_id: form.value.model_id ?? 0,
                            model_name: modelName,
                            code: form.value.code,
                            brand_name: brandName,
                        },
                    });
                },
            },
            props: {
                onConfirm: async () => {
                    await deleteComplectation(complectationId);

                    showSuccess({
                        title: 'Комплектация удалена',
                    });

                    goToComplectationsPage();
                },
            },
        });
    }

    function goToComplectationsPage(): void {
        router.push({ name: 'HandbookComplectations' });
    }

    function fetchComplectation(): void {
        setComplectationLoading(true);

        getComplectation(complectationId).then((complectation: Complectation) => {
            form.value = fillComplectationUIUpdateFormFromRequestComplectation(complectation);

            if (complectation.brand_id === 1) {
                formBrand.value = 'hyundai';
            }
            else if (complectation.brand_id === 2) {
                formBrand.value = 'genesis';
            }

            setComplectationLoading(false);
        });
    }

    function fetchFormOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });
    }

    onMounted(() => {
        fetchComplectation();
        fetchFormOptions();
    });
</script>
